<script lang="ts" setup>
import {
  WdsButtonGhost,
  WdsButtonIcon,
  WdsHeading,
  WdsIcon,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { graphql } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import LoadingList from '@/components/Dashboard/Widgets/Utils/LoadingList.vue'
import { toFormattedAmountByLocale } from '@/utils/currency'

const props = defineProps<{
  hireId: string
}>()

const { result, loading } = useQuery(
  graphql(`
    query HireWorkerPaymentWidgetData($id: ID!) {
      hire(id: $id) {
        id
        currency
        paymentRequests(first: 100) {
          data {
            id
            billedAmount
            expensesAmount
          }
        }
      }
    }
  `),
  () => ({
    id: props.hireId,
  }),
  {
    clientId: 'publicApi',
  },
)

const hire = computed(() => result.value?.hire)
const paymentRequests = computed(() => hire.value?.paymentRequests?.data ?? [])
const currency = computed(() => hire.value?.currency ?? '')

const totalReceived = computed(() =>
  paymentRequests.value.reduce((sum, pr) => sum + (pr.billedAmount ?? 0), 0),
)
const totalExpenses = computed(() =>
  paymentRequests.value.reduce((sum, pr) => sum + (pr.expensesAmount ?? 0), 0),
)

const formattedTotalReceived = computed(() =>
  toFormattedAmountByLocale(totalReceived.value.toFixed(2)),
)
const formattedTotalExpenses = computed(() =>
  toFormattedAmountByLocale(totalExpenses.value.toFixed(2)),
)

const allPaymentRequestsUrl = computed(() => laravelNamedRoute(namedRoutes.billing.bills))
const createPaymentRequestUrl = computed(() =>
  laravelNamedRoute(namedRoutes.bills.create, { hire: props.hireId }),
)
const addExpensesUrl = computed(() => laravelNamedRoute(namedRoutes.expenses.index))
</script>

<template>
  <WdsStack
    direction-column
    class="tw-w-full tw-px-4 tw-py-3 tw-rounded tw-border tw-border-gray-200 tw-bg-true-gray-100"
    data-test="hire-worker-payment-widget"
  >
    <WdsStack justify-space-between align-center>
      <WdsStack gap-sm align-center>
        <WdsIcon name="receipt" :size="20" />
        <WdsHeading type="title3">{{ $t('Payments') }}</WdsHeading>
      </WdsStack>
      <WdsButtonGhost secondary :href="allPaymentRequestsUrl">
        <WdsStack align-center gap-sm>
          <span>{{ $t('See all payment requests') }}</span>
          <WdsIcon name="chevron-right" :size="16" />
        </WdsStack>
      </WdsButtonGhost>
    </WdsStack>

    <WdsStack v-if="loading" direction-column gap-sm class="tw-mt-4">
      <LoadingList :count="3" no-bg />
    </WdsStack>

    <WdsStack v-else direction-column class="tw-mt-4" gap-sm>
      <WdsStack direction-row gap-md class="tw-flex-wrap">
        <WdsStack
          justify-space-between
          align-center
          class="tw-flex-1 tw-min-w-0 tw-bg-white tw-px-4 tw-py-2 tw-rounded"
          data-test="hire-worker-payment-widget-total-received"
        >
          <WdsStack direction-column gap-xs>
            <WdsText small secondary>
              <span class="tw-font-semibold tw-uppercase">{{ $t('Total requested') }}</span>
            </WdsText>
            <WdsText large class="tw-font-semibold tw-text-base">
              {{ currency }} {{ formattedTotalReceived }}
            </WdsText>
          </WdsStack>
          <WdsButtonIcon
            :href="createPaymentRequestUrl"
            data-test="hire-worker-payment-widget-see-all"
            aria-label="Request payment"
          >
            <WdsIcon name="add" :size="20" />
          </WdsButtonIcon>
        </WdsStack>
        <WdsStack
          justify-space-between
          align-center
          class="tw-flex-1 tw-min-w-0 tw-bg-white tw-px-4 tw-py-2 tw-rounded"
          data-test="hire-worker-payment-widget-total-expenses"
        >
          <WdsStack direction-column gap-xs>
            <WdsText small secondary>
              <span class="tw-font-semibold tw-uppercase">{{ $t('Total expenses') }}</span>
            </WdsText>
            <WdsText large class="tw-font-semibold tw-text-base">
              {{ currency }} {{ formattedTotalExpenses }}
            </WdsText>
          </WdsStack>
          <WdsButtonIcon
            :href="addExpensesUrl"
            data-test="hire-worker-payment-widget-add-expenses-card"
            aria-label="Add expenses"
          >
            <WdsIcon name="add" :size="20" />
          </WdsButtonIcon>
        </WdsStack>
      </WdsStack>
    </WdsStack>
  </WdsStack>
</template>
